import { LuNotebookText } from "react-icons/lu";
import { ChevronLeft, Instagram, Linkedin } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import DotsBackground from "../components/DotsBackground.tsx";
import { LessonBox } from "../components/LessonBox.tsx";
import { unitsData } from "../data/lessons/lessonData.ts";
import LeftSidebar from "../components/LeftSidebar.tsx";
import RightSidebar from "../components/RightSidebar.tsx";
import BottomNav from "../components/BottomNav.tsx";
import TopProfileBar from "../components/TopProfileBar.tsx";
import { useNavigate } from "react-router-dom";

import {
  completeLessonFromUI,
  getLessonProgress,
  getLessonState,
  getUnitProgress,
  getUserData,
  resetLessonProgress,
} from "../utils/userStorage.ts";

const HomePage = () => {
  const [currentSection, setCurrentSection] = useState("Belajar");
  const navigate = useNavigate();
  const [headerBg, setHeaderBg] = useState(unitsData[0].colorClassName);
  const [activeUnitIdx, setActiveUnitIdx] = useState(0);
  const unitRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Responsive breakpoints
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLandscape = useMediaQuery({ orientation: "landscape" });
  // console.log("isMobile:", isMobile, "isTablet:", isTablet, "isDesktop:", isDesktop, "isLandscape:", isLandscape);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });

  // Generate units with dynamic lesson states based on user progress
  const generateUnitsWithProgress = useCallback(() => {
    const userData = getUserData();
    const completedLessons = userData?.progress?.coursesCompleted || [];
    return unitsData.map((unit) => {
      const unitLessonIds = unit.lessons.map((lesson) => lesson.id);
      const lessonsWithProgress = unit.lessons.map((lesson) => {
        const state = getLessonState(lesson.id, unitLessonIds);
        const progress = getLessonProgress(lesson.id, unitLessonIds);
        if (state === "on-progress") {
          return {
            ...lesson,
            state,
            currentLesson: progress.currentLesson,
            totalLessons: progress.totalLessons,
          };
        }
        return {
          ...lesson,
          state,
        };
      });
      const unitProgress = getUnitProgress(unitLessonIds);
      return {
        ...unit,
        lessons: lessonsWithProgress,
        progress: unitProgress,
      };
    });
  }, []);

  const [units, setUnits] = useState(generateUnitsWithProgress());

  // Function to handle lesson completion (for testing/demo purposes)
  const handleLessonComplete = useCallback(
    (lessonId: string) => {
      const success = completeLessonFromUI(lessonId, 10);
      if (success) {
        setUnits(generateUnitsWithProgress());
        return true;
      }
      return false;
    },
    [generateUnitsWithProgress]
  );

  // Listen for localStorage changes to keep darkMode in sync
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Listen for localStorage changes to update UI in real-time
  useEffect(() => {
    const handleUserDataChange = () => {
      setUnits(generateUnitsWithProgress());
    };
    window.addEventListener("userDataChanged", handleUserDataChange);
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        setUnits(generateUnitsWithProgress());
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("userDataChanged", handleUserDataChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [generateUnitsWithProgress]);

  // Scroll handler to update header background color
  useEffect(() => {
    const handleScroll = () => {
      if (!unitRefs.current.length) return;
      const header = document.getElementById("main-header");
      const headerRect = header?.getBoundingClientRect();
      const headerHeight = headerRect ? headerRect.height : 120;
      const triggerOffset = 90;
      let activeIdx = 0;
      for (let i = 0; i < unitRefs.current.length; i++) {
        const ref = unitRefs.current[i];
        if (!ref) continue;
        const rect = ref.getBoundingClientRect();
        if (rect.top - headerHeight <= triggerOffset) {
          activeIdx = i;
        }
      }
      setHeaderBg(units[activeIdx].colorClassName);
      setActiveUnitIdx(activeIdx);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [units]);

  return (
    <div
      className={`${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      } flex min-h-screen relative`}
    >
      <DotsBackground darkMode={darkMode} />
      {/* Left Sidebar: desktop shows full, tablet shows compact icon-only, mobile hides */}
      {isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
        />
      )}
      {isTablet && !isMobile && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
          compact
        />
      )}
      <div className="flex-1 flex">
        {/* Top Profile Bar (mobile/tablet only) */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
            }}
          >
            <TopProfileBar darkMode={darkMode}>
              {/* Only show profile section from RightSidebar */}
              <RightSidebar darkMode={darkMode} profileOnly />
            </TopProfileBar>
          </div>
        )}
        <div
          className="flex-1 relative z-10 bg-transparent pt-0 md:pt-0"
          style={isMobile ? { paddingTop: 64, paddingBottom: 64 } : {}}
        >
          {/* Header */}
          <div
            id="main-header"
            className={`sticky ${
              isMobile
                ? "top-20 rounded-lg px-4 py-3 mx-4"
                : "top-10 rounded-xl px-8 py-4 mx-8"
            } z-20 flex items-center justify-between shadow-lg transition-colors duration-300 ${headerBg}`}
            style={{ minHeight: isMobile ? 64 : 90 }}
          >
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <ChevronLeft className="w-5 h-5 opacity-80 text-white" />
                <span
                  className="text-white text-base font-medium opacity-80"
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  Bagian 1, Unit {activeUnitIdx + 1}
                </span>
              </div>
              <div
                className={`text-white leading-tight ${
                  isMobile || isTablet ? "text-lg" : "text-2xl"
                }`}
                style={{ fontFamily: "Feather Bold" }}
              >
                {units[activeUnitIdx].title}
              </div>
            </div>
            <button
              className={`flex items-center justify-center ${headerBg} border-2 gap-2 shadow px-2 md:px-5 py-2 rounded-xl text-white font-semibold text-base duration-200 transform hover:-translate-y-0.5 transition-all`}
              style={{
                borderColor:
                  headerBg === "bg-red-500"
                    ? "rgba(185, 28, 28, 0.6)"
                    : headerBg === "bg-blue-500"
                    ? "rgba(30, 64, 175, 0.6)"
                    : headerBg === "bg-green-500"
                    ? "rgba(21, 128, 61, 0.6)"
                    : headerBg === "bg-purple-500"
                    ? "rgba(107, 33, 168, 0.7)"
                    : headerBg === "bg-pink-500"
                    ? "rgba(190, 24, 93, 0.7)"
                    : headerBg === "bg-orange-500"
                    ? "rgba(194, 65, 12, 0.7)"
                    : headerBg === "bg-teal-500"
                    ? "rgba(13, 148, 136, 0.7)"
                    : undefined,
              }}
              onClick={() => navigate(`/guide/1/${activeUnitIdx + 1}`)}
            >
              <LuNotebookText className="w-7 h-7 md:w-8 md:h-8 text-white" />
              {/* Only show text label if tablet and NOT mobile (i.e. portrait tablet) */}
              {(isTablet && isLandscape) ||
                (!isMobile && (
                  <span style={{ fontFamily: "Feather Bold" }}>Panduan</span>
                ))}
            </button>
          </div>

          {/* Learning Flow Zigzag */}
          <div
            className="relative flex flex-col mt-6 md:mt-10 pt-2"
            style={{
              minHeight: isMobile ? 340 : 0,
              paddingBottom: isMobile ? 300 : 0,
            }}
          >
            {units.map((unit, unitIdx) => (
              <div
                key={unit.title}
                ref={(el) => {
                  unitRefs.current[unitIdx] = el;
                }}
                className="relative flex flex-col items-center"
                style={{ minHeight: isMobile ? 550 : 0 }}
              >
                {/* Unit Title with left/right lines */}
                <div
                  className={`absolute left-0 ${
                    isMobile ? "px-4" : "px-8"
                  } w-full flex items-center justify-center z-0`}
                  style={{ top: 0 }}
                >
                  <div
                    className={`flex-1 h-0.5 mr-2 md:mr-4 ${
                      darkMode ? "bg-gray-600" : "bg-gray-400"
                    }`}
                  />
                  <span
                    className={`text-base md:text-lg font-bold ${
                      darkMode ? "text-gray-200" : "text-gray-700"
                    } px-2 md:px-4 text-center whitespace-nowrap`}
                    style={{ fontFamily: "DIN Next Rounded" }}
                  >
                    {unit.title}
                  </span>
                  <div
                    className={`flex-1 h-0.5 ml-2 md:ml-4 ${
                      darkMode ? "bg-gray-600" : "bg-gray-400"
                    }`}
                  />
                </div>
                {/* Lesson boxes container below the title */}
                <div
                  style={{
                    marginTop: 48,
                    width: isMobile ? 220 : 340,
                    height: isMobile ? 320 : 520,
                    position: "relative",
                  }}
                >
                  {unit.lessons.map((lesson, idx) => {
                    const positions = isMobile
                      ? [
                          { left: "50%", translateX: "-50%", top: 0 },
                          { left: "-40px", translateX: "0", top: 100 },
                          { left: "50%", translateX: "-50%", top: 200 },
                          { left: "170px", translateX: "0", top: 300 },
                          { left: "50%", translateX: "-50%", top: 400 },
                        ]
                      : [
                          { left: "50%", translateX: "-50%", top: 0 },
                          { left: "10px", translateX: "0", top: 100 },
                          { left: "50%", translateX: "-50%", top: 200 },
                          { left: "260px", translateX: "0", top: 300 },
                          { left: "50%", translateX: "-50%", top: 400 },
                        ];
                    const pos = positions[idx] || {
                      left: "50%",
                      translateX: "-50%",
                      top: idx * (isMobile ? 70 : 100),
                    };
                    return (
                      <div
                        key={lesson.lessonTitle}
                        className="absolute z-10"
                        style={{
                          left: pos.left,
                          top: pos.top,
                          transform: `translateX(${pos.translateX})`,
                        }}
                      >
                        {lesson.state === "on-progress" &&
                        "currentLesson" in lesson ? (
                          <LessonBox
                            type={lesson.type}
                            state="on-progress"
                            colorClassName={unit.colorClassName}
                            lessonTitle={lesson.lessonTitle}
                            lessonDescription={lesson.lessonDescription}
                            currentLesson={lesson.currentLesson}
                            totalLessons={lesson.totalLessons}
                            lessonId={lesson.id}
                            darkMode={darkMode}
                          />
                        ) : (
                          <LessonBox
                            type={lesson.type}
                            state={lesson.state as "finish" | "not-started"}
                            colorClassName={unit.colorClassName}
                            lessonTitle={lesson.lessonTitle}
                            lessonDescription={lesson.lessonDescription}
                            lessonId={lesson.id}
                            darkMode={darkMode}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Add gap only after the last unit */}
                {unitIdx === units.length - 1 && (
                  <div style={{ height: isMobile ? 80 : 280 }} />
                )}
              </div>
            ))}
          </div>
          {isMobile && (
            <footer className="w-full pt-2 md:pt-2 pb-12 md:pb-10 bg-transparent flex flex-col items-center gap-2 z-10 relative">
              <div
                className="text-gray-500 text-sm font-semibold"
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                &copy; 2025 Galaxy Developer
              </div>
              <div className="flex gap-4 mt-1">
                <a
                  href="https://www.instagram.com/galaxy.developer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/galaxydeveloper"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </footer>
          )}
        </div>

        {/* Right Sidebar (desktop and tablet only) */}
        {(isDesktop || isTablet) && <RightSidebar darkMode={darkMode} />}
      </div>
      {/* Bottom Nav (mobile only) */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100vw",
            zIndex: 30,
          }}
        >
          <BottomNav
            currentSection={currentSection}
            setCurrentSection={setCurrentSection}
            darkMode={darkMode}
          />
        </div>
      )}
    </div>
  );
};

export default HomePage;
